import React, { useEffect, useState, Suspense } from 'react';
import { useSelector } from 'react-redux';
import {
  changeCourierStatus,
  getCourierWithFilter,
  getZones,
} from 'store/requests/courier';
import { sendLink } from 'store/requests/definitions';
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  SendOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Modal,
  notification,
  Tooltip,
  Row,
  Button,
  Table,
  Col,
  Form,
  Tag,
  Dropdown,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  COURIER_TYPE,
  COURIER_ACTIVE_PASSIVE,
  COURIER_TYPE_COLORS,
  debouncedDataLoadWaitMs,
  ORDER_TYPES,
} from 'helpers/constants';
import {
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { ConfigProvider } from 'antd';
import trTR from 'antd/es/locale/tr_TR';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import ActivePassiveUpdate from 'components/ImportExcel/ActivePassiveUpdate';
import ImportCourierAndCollectorList from 'components/ImportExcel/ImportCourierAndCollectorList';
import { FILE_TEMPLATE_CODE } from 'helpers/constants';

const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const UserAndCourierSendLink = React.lazy(() =>
  import('../../Definitions/Users/UserAndCourierSendLink')
);
const CouriersTableFilters = React.lazy(() => import('./CouriersTableFilters'));

let lastQuery = false;
const { Option } = Select;

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 20,
  },
};

const CourierView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const { confirm } = Modal;
  const [form] = Form.useForm();
  const [ifSelectedCountryHideZone, setIfSelectedCountryHideZone] =
    useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [allZones, setAllZones] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filteringValue, setFilteringValue] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [activePassiveUpdateModalOpen, setActivePassiveUpdateModalOpen] =
    useState(false);
  const [isSendLinkModalOpen, setIsSendLinkModalOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [batchExcelUrl, setBatchExcelUrl] = useState(null);
  const [activePassiveExcelUrl, setActivePassiveExcelUrl] = useState(null);
  const firms = useSelector(
    (state) => state.definitions?.firms?._embedded?.firmResponseDtoList
  );
  const activeStoreId = useSelector(
    (state) => state.auth?.user?.activeStore?.id
  );
  const activeRegionId = useSelector(
    (state) => state.auth?.user?.activeRegion?.id
  );

  const [tableParams, setTableParams] = useState(initialPageParams);
  const openSortFeature = !!rows.length;

  useEffect(() => {
    setBatchExcelUrl(FILE_TEMPLATE_CODE.COURIER_CREATE_OR_UPDATE);
    setActivePassiveExcelUrl(FILE_TEMPLATE_CODE.COURIER_ACTIVE_PASSIVE);
    (async () => {
      const queryFilter = activeStoreId
        ? `store.id==${activeStoreId}`
        : `store.region.id==${activeRegionId}`;
      const zones = await getZones({
        queryString: `filter=${queryFilter} and deleted==false`,
      });
      setAllZones(zones);
      if (zones.length === 1) {
        setSelectedZone(zones[0].id);
        fetchData(initialPageParams, filteringValue, zones[0].id);
      }
    })();
  }, [activeStoreId, activeRegionId]);

  const fetchData = (tableParamsValue, filters, selectedZone) => {
    setFilteringValue(filters);
    const queryString = getQueryString(tableParamsValue);
    loadDataWithDebounce(queryString, filters, selectedZone);
  };

  const columns = [
    {
      title: t('fullName'),
      dataIndex: 'fullName',
      sorter: openSortFeature,
      showSorterTooltip: false,
    },
    {
      title: t('firm'),
      dataIndex: 'firmName',
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (firm) => (firm ? firm : '-'),
    },
    {
      title: t('courierCode'),
      dataIndex: 'employeeNumber',
      sorter: openSortFeature,
      showSorterTooltip: false,
    },
    {
      title: t('phoneNumber'),
      dataIndex: 'phoneNumber',
      sorter: openSortFeature,
      showSorterTooltip: false,
    },
    {
      title: t('courierType'),
      dataIndex: 'courierType',
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (courierType) => (
        <Tag color={COURIER_TYPE_COLORS[courierType]}>
          {t(`courierType_${courierType}`)}
        </Tag>
      ),
    },
    {
      title: t('currentNumber'),
      dataIndex: 'currentNumber',
      sorter: openSortFeature,
      showSorterTooltip: false,
    },
    {
      title: t('vehicleStatus'),
      dataIndex: 'isActive',
      sorter: openSortFeature,
      showSorterTooltip: false,
      render: (isActive) =>
        isActive ? (
          <Tag color={'green'}>{t('active')}</Tag>
        ) : (
          <Tag color={'red'}>{t('passive')}</Tag>
        ),
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('sendLink'),
                  icon: <SendOutlined />,
                  onClick: () => {
                    sendNotificationLink(dataItem?.userId);
                  },
                },
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                  },
                },
                {
                  danger: dataItem.isActive,
                  label: dataItem.isActive ? t('delete') : t('activate'),
                  icon: dataItem.isActive ? (
                    <CloseOutlined />
                  ) : (
                    <CheckOutlined />
                  ),
                  onClick: () => {
                    showChangeCourierStatus({
                      id: selectedItem.id,
                      isActive: !dataItem.isActive,
                    });
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];
  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled: true,
      id: 'downloadButton',
      type: '',
      title: t('download'),
      customContent: (
        <CSVLink
          filename={t('couriersTitle')}
          data={[
            columns.map((item) => item.title),
            ...rows.map((item) => [
              item.fullName,
              item.firmName ? item.firmName : '-',
              item.employeeNumber,
              item.phoneNumber,
              item.courierType,
              item.currentNumber,
              item.isActive ? t('active') : t('passive'),
              item.parentIdentityNumber ?? '-',
            ]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      ),
      onClick: () => {},
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled,
      type: '',
      title: t('sendLink'),
      icon: <SendOutlined />,
      onClick: () => setIsSendLinkModalOpen(true),
      disabled: false,
      hasDropdown: false,
    },
    {
      isCrudEnabled,
      type: '',
      title: t('import'),
      icon: <UploadOutlined />,
      onClick: () => setIsImportModalOpen(true),
      disabled: false,
      hasDropdown: true,
    },
  ];

  const sendNotificationLink = (itemId) => {
    let data = {
      storeCodes: null,
      userIds: [itemId],
      role: 'COURIER',
    };
    sendLink(data).then((res) => {
      if (res.isSuccess) {
        notificationSendLink('success');
      } else {
        notification.error({ message: res.message });
      }
    });
  };

  const notificationSendLink = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('sentLink'),
    });
  };

  const isActiveSelectOptions = Object.entries(COURIER_ACTIVE_PASSIVE).map(
    ([key, value]) => {
      return { value, label: t(`isActive_${key}`) };
    }
  );

  const courierTypeSelectOptions = Object.keys(COURIER_TYPE).map((type) => {
    return { value: type, label: t(`courierType_${type}`) };
  });

  function showChangeCourierStatus(data) {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: !data.isActive ? t('courierDelete') : t('courierActive'),
      okText: !data.isActive ? t('delete') : t('activate'),
      okType: !data.isActive ? 'danger' : 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        changeCourierStatus(data).then((res) => {
          if (res.isSuccess) {
            notificationDelete('success', data.isActive);
            lastQuery = null;
            fetchData(initialPageParams, filteringValue, selectedZone);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  const renderSelectedFilters = () => {
    return Object.keys(filteringValue).map((item, idx) => {
      const value =
        item === 'isActive'
          ? String(filteringValue[item] ?? '')
          : filteringValue[item];
      const firm = firms.find((firm) => firm.id === filteringValue?.[item]);
      if (value) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {item === 'courierType'
              ? `${t(item)} : ${t(`courierType_${value}`)}`
              : item === 'country'
              ? `${t('acrossTurkey')} : ${value ? t('true') : t('false')}`
              : item === 'isActive'
              ? `${t('vehicleStatus')} : ${t(`isActive_${value}`)}`
              : `${t(item)} : ${t(
                  `${item === 'firmName' ? firm?.name : value}`
                )}`}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filteringValue).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filteringValue,
          [destinationItem]: undefined,
        };
        setFilteringValue(editedFilters);
        fetchData(initialPageParams, editedFilters, selectedZone);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const notificationDelete = (type, status) => {
    notification[type]({
      description: status ? t('statusActivated') : t('deletedCourier'),
    });
  };

  const getQueryString = (tableParamsValue) => {
    let queryString = '';

    queryString = `${queryString}&page=${
      tableParamsValue.pagination.current - 1
    }&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue.column) {
      queryString = `${queryString}&sort=${tableParamsValue.column.dataIndex},${
        ORDER_TYPES[tableParamsValue.order]
      }`;
    }
    return queryString;
  };

  const loadDataWithDebounce = (queryString, filters, selectedZone) => {
    setIsFiltersOpen(false);
    const isFiltering =
      filters &&
      Object.keys(filters).some((k) => {
        const v = filters[k];
        return (
          v !== undefined &&
          v !== null &&
          !(typeof v === 'string' && v.trim() === '') &&
          !(Array.isArray(v) && v.length === 0)
        );
      });

    const payload = {
      ...filters,
      zoneId: filters.country ? null : selectedZone,
      userRoleDeleted: false,
      statuses: isFiltering ? ["APPROVED", "LEFT"] : ["APPROVED"],
      userRole: 'COURIER',
      userRoleActive: true,
    };

    setIfSelectedCountryHideZone(!!payload.country);

    delete payload.country;
    setLoading(true);
    getCourierWithFilter(payload, queryString)
      .then((courier) => {
        const { content, page } = courier;
        setRows(content ? content : []);
        setTableParams({
          pagination: {
            total: page.totalElements,
            current: page.number + 1,
            pageSize: page.size,
          },
        });
      })
      .finally(() => setLoading(false));
  };

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      if (item.hasDropdown) {
        return (
          item.isCrudEnabled &&
          (item.customContent ?? (
            <Dropdown
              key={idx}
              menu={{
                items: [
                  {
                    label: t('bulkAddAndUpdate'),
                    icon: <UploadOutlined />,
                    onClick: () => {
                      setIsImportModalOpen(true);
                    },
                  },
                  {
                    label: t('makeActiveOrPassive'),
                    icon: <UnorderedListOutlined />,
                    onClick: () => {
                      setActivePassiveUpdateModalOpen(true);
                    },
                  },
                ],
              }}
              placement="bottomRight"
              arrow
              trigger={['click']}
            >
              <Tooltip title={item.title} key={idx}>
                <Button
                  type={item.type}
                  shape="round"
                  icon={item.icon}
                  style={{ marginBottom: '10px', marginRight: '3px' }}
                  disabled={item.disabled}
                  danger={item.danger}
                />
              </Tooltip>
            </Dropdown>
          ))
        );
      } else {
        return (
          <Tooltip title={item.title} key={idx}>
            {item.isCrudEnabled &&
              (item.customContent ?? (
                <Button
                  type={item?.type}
                  shape="round"
                  icon={item?.icon}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  style={{ marginBottom: '10px', marginRight: '3px' }}
                  danger={item?.danger}
                />
              ))}
          </Tooltip>
        );
      }
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      {!ifSelectedCountryHideZone && (
        <Select
          style={{ width: '30%', marginBottom: 10, marginTop: 10 }}
          showSearch
          placeholder={t('selectZone')}
          optionFilterProp="children"
          required={true}
          loading={!allZones}
          value={selectedZone}
          onChange={(key) => {
            setSelectedZone(key);
            removeItemFromFilters('country');
            fetchData(initialPageParams, filteringValue, key);
          }}
        >
          {allZones &&
            allZones?.map((zone) => (
              <Option key={zone.id} value={zone.id}>
                {`${zone.name} ${
                  zone.active === false ? `(${t('passive')})` : ''
                }`}
              </Option>
            ))}
        </Select>
      )}
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={(pagination, filters, sorter) => {
              fetchData(
                {
                  pagination,
                  ...sorter,
                },
                filteringValue,
                selectedZone
              );
            }}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <CouriersTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            handlerFilter={(filter) => {
              fetchData(initialPageParams, filter, selectedZone);
            }}
            form={form}
            courierTypeSelectOptions={courierTypeSelectOptions}
            isActiveSelectOptions={isActiveSelectOptions}
          />
        )}
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={() =>
              fetchData(initialPageParams, filteringValue, selectedZone)
            }
          />
        )}
        {isEditModalOpen && (
          <Edit
            courier={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={() =>
              fetchData(initialPageParams, filteringValue, selectedZone)
            }
          />
        )}
        {isImportModalOpen && (
          <ImportCourierAndCollectorList
            onCloseHandler={() => setIsImportModalOpen(false)}
            batchExcelUrl={batchExcelUrl}
            onSuccessCallback={() =>
              fetchData(initialPageParams, filteringValue, selectedZone)
            }
            isCourier={true}
          />
        )}
        {isSendLinkModalOpen && (
          <UserAndCourierSendLink
            onCloseHandler={() => setIsSendLinkModalOpen(false)}
            onSuccessCallback={() =>
              fetchData(initialPageParams, filteringValue, selectedZone)
            }
            isCourier={true}
          />
        )}
        {activePassiveUpdateModalOpen && (
          <ActivePassiveUpdate
            onCloseHandler={() => setActivePassiveUpdateModalOpen(false)}
            onSuccessCallback={() =>
              fetchData(initialPageParams, filteringValue, selectedZone)
            }
            activePassiveExcelUrl={activePassiveExcelUrl}
            isCourier={true}
          />
        )}
      </Suspense>
    </div>
  );
};

export default CourierView;
